
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth";

interface UserStatusCardProps {
  togglePremium: (userId: string, isPremium: boolean) => Promise<void>;
  getCurrentUserIsPremium: () => boolean;
  updating: string | null;
}

const UserStatusCard = ({ togglePremium, getCurrentUserIsPremium, updating }: UserStatusCardProps) => {
  const { user, refreshPremiumStatus } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  
  if (!user) return null;

  const handleTogglePremium = async () => {
    setIsUpdating(true);
    try {
      await togglePremium(user.id, !getCurrentUserIsPremium());
      await refreshPremiumStatus();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>Your Account Status</CardTitle>
        <CardDescription>
          Quickly toggle your own premium status for testing
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <Badge variant={getCurrentUserIsPremium() ? "default" : "outline"}>
            {getCurrentUserIsPremium() ? "Premium" : "Free"}
          </Badge>
          <Button 
            variant="outline"
            onClick={handleTogglePremium}
            disabled={updating === user.id || isUpdating}
          >
            {updating === user.id || isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                Toggle to {getCurrentUserIsPremium() ? "Free" : "Premium"}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserStatusCard;
